import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// ── Vista simplificada para el técnico ────────────────────────────────────────
const PanelTecnico: React.FC = () => {
  const [cortes, setCortes] = useState<any[]>([]);
  const [averias, setAverias] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cortRes, avRes] = await Promise.all([
          api.get('/cortes/pendientes-tecnico'),
          api.get('/averias/mis-averias'),
        ]);
        setCortes(cortRes.data || []);
        setAverias((avRes.data || []).filter((a: any) => ['pendiente', 'en_proceso'].includes(a.estado)));
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div style={{ color:'#64748b', textAlign:'center', padding:48 }}>Cargando...</div>;

  const ESTADO_COLOR: Record<string, string> = {
    pendiente_corte: '#f59e0b',
    pendiente_reconexion: '#38bdf8',
    pendiente: '#f59e0b',
    en_proceso: '#a78bfa',
  };

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ color:'#f1f5f9', margin:0, fontSize:24, fontWeight:800 }}>🔧 Mi Panel de Trabajo</h2>
        <p style={{ color:'#64748b', margin:'4px 0 0', fontSize:13 }}>Órdenes y averías asignadas a tu rol</p>
      </div>

      {/* KPIs técnico */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:14, marginBottom:24 }}>
        {[
          { label:'Cortes pendientes', value: cortes.filter(c => c.estado === 'pendiente_corte').length, icon:'✂️', color:'#f59e0b' },
          { label:'Reconexiones pend.', value: cortes.filter(c => c.estado === 'pendiente_reconexion').length, icon:'🔌', color:'#38bdf8' },
          { label:'Averías activas', value: averias.length, icon:'🔧', color:'#a78bfa' },
        ].map(s => (
          <div key={s.label} style={{ background:'#1e293b', borderRadius:12, padding:'16px 20px', border:`1px solid ${s.color}33` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ color:'#64748b', fontSize:12, marginBottom:6 }}>{s.label}</div>
                <div style={{ color:s.color, fontSize:28, fontWeight:800 }}>{s.value}</div>
              </div>
              <span style={{ fontSize:28 }}>{s.icon}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* Órdenes pendientes */}
        <div style={{ background:'#1e293b', borderRadius:12, border:'1px solid #334155', overflow:'hidden' }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid #334155' }}>
            <span style={{ color:'#f1f5f9', fontWeight:700, fontSize:15 }}>✂️ Órdenes pendientes</span>
          </div>
          <div style={{ maxHeight:360, overflowY:'auto' }}>
            {cortes.length === 0 ? (
              <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>✅ Sin órdenes pendientes</div>
            ) : cortes.map(c => (
              <div key={c.id} style={{ padding:'12px 18px', borderBottom:'1px solid #0f172a' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ color:'#f1f5f9', fontWeight:600, fontSize:13 }}>{c.cliente?.nombres} {c.cliente?.apellidos}</div>
                  <span style={{ background: (ESTADO_COLOR[c.estado] || '#64748b') + '22', color: ESTADO_COLOR[c.estado] || '#64748b', border:`1px solid ${ESTADO_COLOR[c.estado] || '#64748b'}44`, borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:700 }}>
                    {c.estado === 'pendiente_corte' ? '✂️ Corte' : '🔌 Reconexión'}
                  </span>
                </div>
                <div style={{ color:'#64748b', fontSize:11, marginTop:2 }}>{c.cliente?.codigo} — {c.motivo || 'Sin motivo'}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Averías activas */}
        <div style={{ background:'#1e293b', borderRadius:12, border:'1px solid #334155', overflow:'hidden' }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid #334155' }}>
            <span style={{ color:'#f1f5f9', fontWeight:700, fontSize:15 }}>🔧 Averías activas</span>
          </div>
          <div style={{ maxHeight:360, overflowY:'auto' }}>
            {averias.length === 0 ? (
              <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>✅ Sin averías activas</div>
            ) : averias.map((a: any) => (
              <div key={a.id} style={{ padding:'12px 18px', borderBottom:'1px solid #0f172a' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ color:'#f1f5f9', fontWeight:600, fontSize:13 }}>{a.titulo}</div>
                  <span style={{ background: (ESTADO_COLOR[a.estado] || '#64748b') + '22', color: ESTADO_COLOR[a.estado] || '#64748b', border:`1px solid ${ESTADO_COLOR[a.estado] || '#64748b'}44`, borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:700 }}>
                    {a.estado}
                  </span>
                </div>
                <div style={{ color:'#64748b', fontSize:11, marginTop:2 }}>{a.tipo} — {a.cliente?.nombres} {a.cliente?.apellidos}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Panel financiero para admin/gerente/cobrador ──────────────────────────────
const PanelAdmin: React.FC = () => {
  const [stats, setStats] = useState({ clientes: 0, activos: 0, cortados: 0, facturasPendientes: 0, porCobrar: 0, cobradoMes: 0, pagosHoy: 0 });
  const [ultimosPagos, setUltimosPagos] = useState<any[]>([]);
  const [morosos, setMorosos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/panel/stats');
      setStats({
        clientes: data.clientes,
        activos: data.activos,
        cortados: data.cortados,
        facturasPendientes: data.facturasPendientes,
        porCobrar: data.porCobrar,
        cobradoMes: data.cobradoMes,
        pagosHoy: data.pagosHoy,
      });
      setMorosos(data.morosos);
      setUltimosPagos(data.ultimosPagos);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  if (loading) return <div style={{ color:'#64748b', textAlign:'center', padding:48 }}>Cargando panel...</div>;

  const morososConUbicacion = morosos.filter((c: any) => c.lat && c.lng);

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ color:'#f1f5f9', margin:0, fontSize:24, fontWeight:800 }}>📊 Panel Principal</h2>
        <p style={{ color:'#64748b', margin:'4px 0 0', fontSize:13 }}>Resumen general del sistema de agua</p>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:14, marginBottom:24 }}>
        {[
          { label:'Total Clientes', value: stats.clientes, icon:'👥', color:'#38bdf8' },
          { label:'Activos', value: stats.activos, icon:'✅', color:'#22c55e' },
          { label:'Cortados', value: stats.cortados, icon:'✂️', color:'#ef4444' },
          { label:'Facturas Pendientes', value: stats.facturasPendientes, icon:'🧾', color:'#f59e0b' },
          { label:'Por Cobrar', value: `S/. ${stats.porCobrar.toFixed(2)}`, icon:'💰', color:'#f59e0b' },
          { label:'Cobrado este Mes', value: `S/. ${stats.cobradoMes.toFixed(2)}`, icon:'📅', color:'#22c55e' },
          { label:'Cobrado Hoy', value: `S/. ${stats.pagosHoy.toFixed(2)}`, icon:'💵', color:'#a78bfa' },
          { label:'Morosos', value: morosos.length, icon:'⚠️', color:'#ef4444' },
        ].map(s => (
          <div key={s.label} style={{ background:'#1e293b', borderRadius:12, padding:'16px 20px', border:`1px solid ${s.color}33` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ color:'#64748b', fontSize:12, marginBottom:6 }}>{s.label}</div>
                <div style={{ color:s.color, fontSize:22, fontWeight:800 }}>{s.value}</div>
              </div>
              <span style={{ fontSize:26 }}>{s.icon}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
        {/* Mapa de morosos */}
        <div style={{ background:'#1e293b', borderRadius:12, border:'1px solid #334155', overflow:'hidden' }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid #334155', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ color:'#f1f5f9', fontWeight:700, fontSize:15 }}>⚠️ Mapa de morosos</span>
            <span style={{ background:'#ef444422', color:'#ef4444', border:'1px solid #ef444433', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:700 }}>{morosos.length} morosos</span>
          </div>
          {morososConUbicacion.length > 0 ? (
            <MapContainer center={[-9.0, -75.0]} zoom={6} style={{ height:300 }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {morososConUbicacion.map((c: any) => (
                <CircleMarker key={c.id}
                  center={[Number(c.lat), Number(c.lng)]}
                  radius={10} color="#ef4444" fillColor="#ef4444" fillOpacity={0.7}>
                  <Popup>
                    <div style={{ fontSize:13 }}>
                      <strong>{c.nombres} {c.apellidos}</strong><br/>
                      {c.direccion}<br/>
                      📱 {c.telefono}<br/>
                      Plan: {c.paquete?.nombre}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          ) : (
            <div style={{ height:300, display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b', flexDirection:'column', gap:8 }}>
              <span style={{ fontSize:40 }}>✅</span>
              <span>Sin morosos con ubicacion registrada</span>
            </div>
          )}
        </div>

        {/* Lista morosos */}
        <div style={{ background:'#1e293b', borderRadius:12, border:'1px solid #334155', overflow:'hidden' }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid #334155' }}>
            <span style={{ color:'#f1f5f9', fontWeight:700, fontSize:15 }}>📋 Lista de morosos</span>
          </div>
          <div style={{ maxHeight:300, overflowY:'auto' }}>
            {morosos.length === 0 ? (
              <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>Sin morosos</div>
            ) : morosos.map((c: any) => (
              <div key={c.id} style={{ padding:'12px 18px', borderBottom:'1px solid #0f172a', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ color:'#f1f5f9', fontWeight:600, fontSize:14 }}>{c.nombres} {c.apellidos}</div>
                  <div style={{ color:'#64748b', fontSize:12 }}>📱 {c.telefono || '—'} · {c.paquete?.nombre}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ color:'#ef4444', fontWeight:700 }}>S/. {Number(c.paquete?.precio || 0).toFixed(2)}</div>
                  <div style={{ color:'#64748b', fontSize:11 }}>pendiente</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ultimos pagos */}
      <div style={{ background:'#1e293b', borderRadius:12, border:'1px solid #334155', overflow:'hidden' }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid #334155' }}>
          <span style={{ color:'#f1f5f9', fontWeight:700, fontSize:15 }}>💵 Ultimos pagos recibidos</span>
        </div>
        {ultimosPagos.length === 0 ? (
          <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>Sin pagos registrados</div>
        ) : ultimosPagos.map(p => (
          <div key={p.id} style={{ padding:'12px 18px', borderBottom:'1px solid #0f172a', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ color:'#f1f5f9', fontWeight:600, fontSize:14 }}>{p.cliente?.nombres} {p.cliente?.apellidos}</div>
              <div style={{ color:'#64748b', fontSize:12 }}>{p.cobrador?.nombre} · {p.metodo}</div>
              <div style={{ color:'#475569', fontSize:11 }}>
                {new Date(p.createdAt).toLocaleString('es-PE', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
              </div>
            </div>
            <div style={{ color:'#22c55e', fontWeight:800, fontSize:16 }}>S/. {Number(p.monto).toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Dispatcher: separa roles sin romper Rules of Hooks ────────────────────────
const Panel: React.FC = () => {
  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
  return usuario.rol === 'tecnico' ? <PanelTecnico /> : <PanelAdmin />;
};

export default Panel;